import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { parseJsonResponse } from '../../services/api';
import './CertifiedProfile.css';

const PILLAR_COLORS = {
  voice_safety: '#10b981',
  emotional_safety: '#06b6d4',
  growth_safety: '#8b5cf6',
  boundary_safety: '#f59e0b',
  identity_safety: '#ec4899'
};

const PILLAR_SVG_ICONS = {
  voice_safety: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>,
  emotional_safety: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
  growth_safety: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  boundary_safety: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  identity_safety: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
};

const StarRating = ({ rating, size = 22 }) => (
  <div className="cp-stars">
    {[...Array(5)].map((_, i) => (
      <svg key={i} viewBox="0 0 24 24" width={size} height={size}
        fill={i < rating ? '#f59e0b' : '#e5e7eb'}
        stroke={i < rating ? '#f59e0b' : '#d1d5db'}
        strokeWidth="0.5">
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
      </svg>
    ))}
  </div>
);

const CertifiedProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`/api/certification/public/${id}`);
        const body = await parseJsonResponse(res);
        if (!res.ok) {
          throw new Error(body.error || 'Certificate not found');
        }
        setData(body);
      } catch (err) {
        console.error('CertifiedProfile fetch error:', err.message);
        setError('Certificate not found. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [id]);

  if (loading) {
    return (
      <div className="cp-page cp-center">
        <div className="cp-spinner" />
        <p>Loading certificate...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cp-page cp-center">
        <div className="cp-error-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5" width="48" height="48"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
        </div>
        <h2>Certificate Not Found</h2>
        <p>{error}</p>
        <button className="cp-btn-primary" onClick={() => navigate('/certification')}>View Certification Program</button>
      </div>
    );
  }

  const evalDate = new Date(data.evaluated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const validDate = data.valid_until ? new Date(data.valid_until).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null;

  return (
    <div className="cp-page">
      {/* Decorative bg elements */}
      <div className="cp-bg-deco">
        <div className="cp-bg-circle cp-bg-c1" />
        <div className="cp-bg-circle cp-bg-c2" />
      </div>

      {/* Navbar */}
      <nav className="cp-nav">
        <div className="cp-nav-inner">
          <div className="cp-nav-brand" onClick={() => navigate('/')}>
            <div className="cp-nav-logo">R</div>
            <span>Mend</span>
          </div>
          <a href="/certification" className="cp-nav-link">Certification Program</a>
        </div>
      </nav>

      {/* Content */}
      <div className="cp-content">
        {/* Main Certificate Card */}
        <div className="cp-card cp-main-card">
          <div className="cp-verified-row">
            <div className="cp-verified-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" width="16" height="16"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              Verified by Mend
            </div>
            <span className={`cp-status cp-status-${data.status}`}>
              {data.status === 'certified' ? 'Certified' : data.status === 'not_certified' ? 'Not Certified' : data.status}
            </span>
          </div>

          <div className="cp-cert-hero">
            <div className="cp-score-ring">
              <svg viewBox="0 0 140 140">
                <circle cx="70" cy="70" r="60" fill="none" stroke="#f3f4f6" strokeWidth="10"/>
                <circle cx="70" cy="70" r="60" fill="none" stroke="#10b981" strokeWidth="10"
                  strokeDasharray={`${(data.overall_score / 100) * 377} 377`}
                  strokeLinecap="round" transform="rotate(-90 70 70)"/>
              </svg>
              <div className="cp-score-num">{Math.round(data.overall_score)}</div>
              <div className="cp-score-lbl">Overall</div>
            </div>
            <div className="cp-cert-info">
              <h1>{data.org_name}</h1>
              <div className="cp-cert-title">Psychologically Safe Workplace&trade;</div>
              <StarRating rating={data.star_rating} size={26} />
              <span className="cp-star-label">{data.star_rating > 0 ? `${data.star_rating}-Star Certified` : 'Evaluation Complete'}</span>
            </div>
          </div>

          {/* Badge */}
          {data.certificate_id && (
            <div className="cp-badge-section">
              <img src={`/api/certification/badge/${data.certificate_id}`} alt="PSW Badge" className="cp-badge-img" />
            </div>
          )}

          {/* Meta */}
          <div className="cp-meta-row">
            <div className="cp-meta">
              <span className="cp-meta-label">Evaluated</span>
              <span className="cp-meta-val">{evalDate}</span>
            </div>
            {validDate && (
              <div className="cp-meta">
                <span className="cp-meta-label">Valid Until</span>
                <span className="cp-meta-val">{validDate}</span>
              </div>
            )}
            <div className="cp-meta">
              <span className="cp-meta-label">Certificate ID</span>
              <span className="cp-meta-val cp-meta-id">{data.certificate_id}</span>
            </div>
          </div>
        </div>

        {/* Pillar Scores */}
        <div className="cp-pillars-section">
          <h2>Safety Pillar Scores</h2>
          <div className="cp-pillars-grid">
            {(data.pillars || []).map(p => (
              <div key={p.pillar} className="cp-card cp-pillar-card" style={{ '--pcolor': PILLAR_COLORS[p.pillar] }}>
                <div className="cp-pillar-top">
                  <div className="cp-pillar-icon" style={{ background: PILLAR_COLORS[p.pillar] + '18', color: PILLAR_COLORS[p.pillar] }}>
                    {PILLAR_SVG_ICONS[p.pillar]}
                  </div>
                  <div>
                    <h3>{p.label}</h3>
                    <span className="cp-pillar-desc">{p.description}</span>
                  </div>
                </div>
                <div className="cp-pillar-score-row">
                  <span className="cp-pillar-num" style={{ color: PILLAR_COLORS[p.pillar] }}>{Math.round(p.score)}</span>
                  <div className="cp-pillar-bar">
                    <div className="cp-pillar-fill" style={{ width: `${p.score}%`, background: PILLAR_COLORS[p.pillar] }} />
                  </div>
                </div>
                {p.evidence_summary && <p className="cp-pillar-evidence">{p.evidence_summary}</p>}
              </div>
            ))}
          </div>
        </div>

        {/* Download */}
        {data.certificate_id && data.status === 'certified' && (
          <div className="cp-download">
            <a href={`/api/certification/pdf/${data.certificate_id}`} target="_blank" rel="noreferrer" className="cp-btn-primary">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download Certificate (PDF)
            </a>
          </div>
        )}

        {/* Footer */}
        <footer className="cp-footer">
          <p>Psychologically Safe Workplace&trade; Certification by <strong>Mend</strong></p>
          <a href="/certification">Learn more about our certification program</a>
        </footer>
      </div>
    </div>
  );
};

export default CertifiedProfile;
